import { useState } from 'react';
import {
  EXOTIC_BETS,
  ExoticBet,
  QuaddieRow,
  StraightExoticRow,
  isQuaddieBet,
  usePredictedExotics,
} from '../../queries/predictedExotics';
import { PredictedExoticsDateRangeControls } from './PredictedExoticsDateRangeControls';
import { ExoticStatsCards } from './ExoticStatsCards';
import { ExoticRacesTable } from './ExoticRacesTable';
import { QuaddieTable } from './QuaddieTable';
import { computeStats, formatResultSummary, getDefaultDates } from './predicted-exotics-helpers';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const DESCRIPTIONS: Record<ExoticBet, string> = {
  'first-four': 'Races where all three analysis types picked the same 4 horses — and how they finished.',
  trifecta: 'Races where all three analysis types picked the same top 3 — and how they finished.',
  quinella: 'Races where all three analysis types picked the same top 2 — and how they finished.',
  'lean-quaddie': 'Quaddie legs narrowed to the horses every analysis type picked.',
  'long-quaddie': 'Quaddie legs covering every horse any analysis type picked.',
};

export function PredictedExoticsPage() {
  const defaults = getDefaultDates();
  const [bet, setBet] = useState<ExoticBet>('first-four');
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [appliedStart, setAppliedStart] = useState(defaults.start);
  const [appliedEnd, setAppliedEnd] = useState(defaults.end);

  const { data, isFetching, error } = usePredictedExotics(bet, appliedStart, appliedEnd);
  const rows = data ?? [];
  const quaddie = isQuaddieBet(bet);
  const target = EXOTIC_BETS.find((b) => b.value === bet)!.target;
  const noun = quaddie ? 'Quaddie' : 'Race';
  const stats = computeStats(rows, target);

  const handleApply = () => {
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Predicted Exotics" description={DESCRIPTIONS[bet]} />

      <div className="flex gap-1 overflow-x-auto border-b border-border pb-2">
        {EXOTIC_BETS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setBet(option.value)}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              bet === option.value
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <PredictedExoticsDateRangeControls
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={handleApply}
        loading={isFetching}
      />

      {!isFetching && !error && rows.length > 0 && <ExoticStatsCards stats={stats} noun={noun} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Results</CardTitle>
          <p className="text-sm text-muted-foreground">
            {isFetching ? 'Loading…' : formatResultSummary(stats, noun.toLowerCase())}
          </p>
        </CardHeader>
        <CardContent>
          {isFetching ? (
            <Spinner label="Loading data…" />
          ) : error ? (
            <div className="flex h-48 items-center justify-center text-destructive">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </div>
          ) : quaddie ? (
            <QuaddieTable rows={rows as QuaddieRow[]} />
          ) : (
            <ExoticRacesTable rows={rows as StraightExoticRow[]} bet={bet} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
